import React, { useState, useRef, useLayoutEffect } from "react";
import _ from "lodash";
import {
  Button,
  TextInput,
  Grid,
  Table,
  Text,
  ActionIcon,
  Group,
  Textarea,
} from "@mantine/core";
import ReactJson from "react-json-view";
import { useForceUpdate } from "@mantine/hooks";
import {
  IconCircleMinus,
  IconCirclePlus,
  IconExchange,
  IconMultiplier1x,
  IconTrash,
  IconPageBreak,
} from "@tabler/icons";

/** Avoid sync loops: form vs local row data often differs by number vs numeric string (API/Mantine). */
const arrayTableRowsLooselyEqual = (a, b) => {
  if (a === b) return true;
  return _.isEqualWith(a, b, (av, bv) => {
    if (av === bv) return true;
    if (av === "" || bv === "") return av === bv;
    if (typeof av === "number" && typeof bv === "string") {
      const n = Number(bv);
      return !Number.isNaN(n) && av === n;
    }
    if (typeof bv === "number" && typeof av === "string") {
      const n = Number(av);
      return !Number.isNaN(n) && bv === n;
    }
    return undefined;
  });
};

const PageBreakRow = ({ index, onRemoveRow, fields, readonly = false }) => {
  return (
    <tr valign="top" width="100%">
      <td colSpan={fields.length + 1}>
        <Group
          position="center"
          spacing="xs"
          style={{
            borderTop: "2px dashed #666",
            borderBottom: "2px dashed #666",
            padding: "4px 0",
            margin: "4px 0",
          }}
        >
          <IconPageBreak size={14} color="#999" />
          <Text size="xs" color="dimmed">
            Page Break
          </Text>
        </Group>
      </td>
      <td>
        {!readonly && (
          <Group ml={"xl"} spacing={5}>
            <ActionIcon size="xs" onClick={() => onRemoveRow(index)}>
              <IconCircleMinus />
            </ActionIcon>
          </Group>
        )}
      </td>
    </tr>
  );
};

const Row = ({
  data,
  onRemoveRow,
  onInsertRow,
  onInsertPageBreak,
  index,
  fields,
  onDataChange,
  readonly = false,
  enablePageBreak = false,
}) => {
  if (data.isPageBreak) {
    return (
      <PageBreakRow
        index={index}
        onRemoveRow={onRemoveRow}
        fields={fields}
        readonly={readonly}
      />
    );
  }

  return (
    <tr valign="top" width="100%">
      <td>
        <Text size={"xs"}> {index + 1}</Text>
      </td>

      {fields.map((f) => (
        <td>
          <f.component
            disabled={readonly}
            row={data}
            rowIndex={index}
            {...(f.type === "checkbox"
              ? { checked: _.get(data, f.fieldName) }
              : { value: _.get(data, f.fieldName) })}
            onChange={(e) => {
              // console.log("onChange", f.fieldName, e);
              const newV = e?.currentTarget
                ? f.type === "checkbox"
                  ? e.currentTarget.checked
                  : e.currentTarget.value
                : e;
              _.set(data, f.fieldName, newV);
              // console.log("onChange > data", data, newV, f.type);
              onDataChange(index, data, f.fieldName);
            }}
            {...f.props}
          />
        </td>
      ))}

      <td>
        {!readonly && (
          <Group ml={"xl"} spacing={5} noWrap>
            <ActionIcon size="xs" onClick={() => onRemoveRow(index)}>
              <IconCircleMinus />
            </ActionIcon>
            <ActionIcon size="xs" onClick={() => onInsertRow(index)}>
              <IconCirclePlus />
            </ActionIcon>
            {enablePageBreak && (
              <ActionIcon
                size="xs"
                onClick={() => onInsertPageBreak(index)}
                title="Insert page break after this row"
              >
                <IconPageBreak />
              </ActionIcon>
            )}
          </Group>
        )}
      </td>
    </tr>
  );
};

const ArrayTable = ({
  form,
  name,
  rowInitValue,
  fields,
  onDataChange,
  onRemoveRow,
  onInsertRow,
  readonly = false,
  enablePageBreak = false,
  addButtonLabel = "Add",
  addButtonProps = {},
}) => {
  const [arrayValues, setArrayValues] = useState([]);
  const arrayValuesRef = useRef(arrayValues);
  arrayValuesRef.current = arrayValues;
  const formValue = _.get(form.values, name);
  /** Deep snapshot to ignore formValue reference-only churn (e.g. costTotal updates). */
  const prevFormValueDeepRef = useRef(undefined);
  /** After first pull from form, allow pushing local [] vs non-empty (user clears rows). */
  const hydratedFromFormRef = useRef(false);

  // const forceUpdate = useForceUpdate();

  const addRow = () => {
    form.markUserEdited?.();
    const currentValues = Array.isArray(arrayValues) ? arrayValues : [];
    setArrayValues([...currentValues, { ...rowInitValue }]);
  };

  const removeRow = (index) => {
    if (!window.confirm("Are you sure you want to remove this row?")) return;
    form.markUserEdited?.();
    const d = [...arrayValues];
    d.splice(index, 1);
    setArrayValues(d);
    if (onRemoveRow) return onRemoveRow(d, index);
  };

  const insertRow = (index) => {
    form.markUserEdited?.();
    const d = [...arrayValues];
    d.splice(index, 0, {
      ...rowInitValue,
    });
    setArrayValues(d);
    if (onInsertRow) return onInsertRow(d, index);
  };

  const insertPageBreak = (index) => {
    form.markUserEdited?.();
    const d = [...arrayValues];
    d.splice(index + 1, 0, { isPageBreak: true });
    setArrayValues(d);
  };

  const dataChange = (index, data, fieldName) => {
    form.markUserEdited?.();
    const d = [...arrayValues];
    d.splice(index, 1, data);
    setArrayValues(d);
    if (onDataChange) return onDataChange(d, index, fieldName);
  };

  // Push local → form in layout *before* pull. If pull ran first (previous implementation),
  // stale form.values would overwrite fresh arrayValues before useEffect push — Save sent old data
  // (e.g. descriptions / QuotationDescription).
  useLayoutEffect(() => {
    if (readonly) return;
    const current = _.get(form.values, name);
    if (!Array.isArray(current)) {
      form.setFieldValue(name, arrayValues);
      return;
    }
    if (arrayTableRowsLooselyEqual(arrayValues, current)) return;
    if (
      arrayValues.length === 0 &&
      current.length > 0 &&
      !hydratedFromFormRef.current
    ) {
      return;
    }
    form.setFieldValue(name, arrayValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form stable; read form.values inside
  }, [arrayValues, name, readonly]);

  // Pull form → local when the slice deep-changes (ignore reference-only churn).
  useLayoutEffect(() => {
    if (!Array.isArray(formValue)) {
      return;
    }
    if (
      prevFormValueDeepRef.current !== undefined &&
      _.isEqual(formValue, prevFormValueDeepRef.current)
    ) {
      return;
    }
    prevFormValueDeepRef.current = formValue;
    if (arrayTableRowsLooselyEqual(formValue, arrayValuesRef.current)) {
      hydratedFromFormRef.current = true;
      return;
    }
    setArrayValues(formValue);
    hydratedFromFormRef.current = true;
  }, [formValue]);

  // Re-hydrate from form after server reload (e.g. save → loadFormData).
  useLayoutEffect(() => {
    hydratedFromFormRef.current = false;
    prevFormValueDeepRef.current = undefined;
  }, [form?._reloadKey]);

  const ths = (
    <tr>
      <th width="5%" align="left">
        <Text size="xs">#</Text>
      </th>
      {fields.map((f) => (
        <th width={f.headerWidth} align="left">
          <Text size="xs">{f.header}</Text>
        </th>
      ))}
      <th width="10%"></th>
    </tr>
  );

  return (
    <>
      <Table style={{ width: "100%" }}>
        <thead>{ths}</thead>
        <tbody>
          {Array.isArray(arrayValues) &&
            arrayValues.map((v, index) => (
              <Row
                key={index}
                data={v}
                index={index}
                onRemoveRow={removeRow}
                onInsertRow={insertRow}
                onInsertPageBreak={insertPageBreak}
                onDataChange={dataChange}
                fields={fields}
                readonly={readonly}
                enablePageBreak={enablePageBreak}
              />
            ))}
        </tbody>
      </Table>

      {!readonly && (
        <Button
          size="xs"
          onClick={() => addRow()}
          mt="xl"
          w="fit-content"
          {...addButtonProps}
        >
          {addButtonLabel}
        </Button>
      )}
    </>
  );
};

export default ArrayTable;
